import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import { Step, StepsRef } from "@design-system/Steps/types";
import { Steps } from "@design-system/Steps";
import { useClientPricing } from "@hooks/billing/useClientPricing";
import { useOrganizationPlansQuery } from "@api/billingOrganization.api";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { StandardModal } from "@design-system/Modal/Modal";
import { PlansOverviewModal } from "@components/billing/PlansOverviewModal/PlansOverviewModal";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { FlatRatePlan } from "@common/schemas/billing.schema";
import { Button } from "@design-system/Button/Button";
import { fromIntToMonetary } from "@common/helpers/monetary";
import {
    currencyFormatter,
    currencyFormatterNoFractions,
} from "@common/helpers/currency";
import priceSectionStyles from "@components/billing/Plans/PriceSection.module.scss";
import { AddClientModalFooter } from "./AddClientModalFooter";
import styles from "./AddClientModal.module.scss";
import {
    AddClientSteps,
    ClientFormSchema,
    clientStepSchema,
    inviteStepSchema,
    planStepSchema,
    StepSchema,
    transferBillingStepSchema,
} from "./AddClientSteps/lib";
import { ClientStep } from "./AddClientSteps/ClientStep";
import { PlanStep } from "./AddClientSteps/PlanStep";
import { InviteStep } from "./AddClientSteps/InviteStep";
import { BillingTransferStep } from "./AddClientSteps/BillingTransferStep";
import {
    getAvailablePlans,
    isOrganizationOwnedPricingBlocked,
} from "./AddClientSteps/planHelpers";

interface Props {
    currentStep: AddClientSteps;
    handleStepChange: (step: AddClientSteps) => void;
    loadingAction: "invite" | "skip" | null;
    submitWithoutInvite: () => Promise<void>;
}

export const AddClientStepsContainer: FC<Props> = ({
    currentStep,
    handleStepChange,
    loadingAction,
    submitWithoutInvite,
}) => {
    const stepsRef = useRef<StepsRef>(null);
    const { values, setFieldValue } = useFormikContext<ClientFormSchema>();
    const clientPricing = useClientPricing();
    const activeOrganization = useActiveOrganization();
    const { data: plansResponse } = useOrganizationPlansQuery(
        activeOrganization?.id,
    );
    const plans = plansResponse?.plans;
    const [isPlansOverviewModalOpen, setIsPlansOverviewModalOpen] =
        useState(false);

    const plansForOverview = useMemo(() => {
        if (!plans) {
            return [];
        }

        const availablePlans = getAvailablePlans(
            activeOrganization?.allowedPlans,
            {
                isOrganizationOwned: values.plan.billingOwnership,
                includeRestrictedPlans: false,
            },
        );

        return plans.filter((plan) =>
            availablePlans.some((available) => available === plan.plan),
        );
    }, [plans, activeOrganization?.allowedPlans, values.plan.billingOwnership]);

    const steps: Array<Step<AddClientSteps>> = useMemo(
        () => [
            {
                id: AddClientSteps.CLIENT,
                header: "Client",
                content: <ClientStep mode="newClient" />,
            },
            {
                id: AddClientSteps.PLAN,
                header: "Plan",
                content: <PlanStep clientPricing={clientPricing} />,
            },
            values.plan.billingOwnership
                ? {
                      id: AddClientSteps.INVITE,
                      header: "Invite",
                      content: <InviteStep />,
                  }
                : {
                      id: AddClientSteps.BILLING_TRANSFER,
                      header: "Invite",
                      content: <BillingTransferStep />,
                  },
        ],
        [values.plan.billingOwnership, clientPricing],
    );

    const stepValidators: Record<AddClientSteps, StepSchema> = useMemo(
        () => ({
            [AddClientSteps.CLIENT]: clientStepSchema,
            [AddClientSteps.PLAN]: planStepSchema,
            [AddClientSteps.INVITE]: inviteStepSchema,
            [AddClientSteps.BILLING_TRANSFER]: transferBillingStepSchema,
        }),
        [],
    );

    const next = useCallback(() => {
        const map: Record<AddClientSteps, AddClientSteps | null> = {
            [AddClientSteps.CLIENT]: AddClientSteps.PLAN,
            [AddClientSteps.PLAN]: values.plan.billingOwnership
                ? AddClientSteps.INVITE
                : AddClientSteps.BILLING_TRANSFER,
            [AddClientSteps.INVITE]: null,
            [AddClientSteps.BILLING_TRANSFER]: null,
        };

        const nextStep = map[currentStep];

        if (nextStep) {
            stepsRef.current?.go(nextStep);
        }
    }, [currentStep, values.plan]);

    const prev = useCallback(() => {
        const map: Record<AddClientSteps, AddClientSteps | null> = {
            [AddClientSteps.CLIENT]: null,
            [AddClientSteps.PLAN]: AddClientSteps.CLIENT,
            [AddClientSteps.INVITE]: AddClientSteps.PLAN,
            [AddClientSteps.BILLING_TRANSFER]: AddClientSteps.PLAN,
        };

        const prevStep = map[currentStep];

        if (prevStep) {
            stepsRef.current?.go(prevStep);
        }
    }, [currentStep]);

    const handlePlanSelect = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            const availablePlans = getAvailablePlans(
                activeOrganization?.allowedPlans,
                {
                    isOrganizationOwned: values.plan.billingOwnership,
                    includeRestrictedPlans: false,
                },
            );
            const isAvailable = availablePlans.some(
                (available) => available === plan,
            );

            if (!isAvailable) {
                return null;
            }

            setFieldValue("plan.plan", plan);
            setIsPlansOverviewModalOpen(false);
        },
        [
            activeOrganization?.allowedPlans,
            setFieldValue,
            values.plan.billingOwnership,
        ],
    );

    const renderSelectPlanAction = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            const label = `Select ${flatRatePlanNames[plan]}`;
            const handleClick = () => handlePlanSelect(plan);

            return plan === FLAT_RATE_PLANS.FREE ? (
                <Button variant="primary" size="md" onClick={handleClick}>
                    {label}
                </Button>
            ) : (
                <Button variant="tertiary" size="md" onClick={handleClick}>
                    {label}
                </Button>
            );
        },
        [handlePlanSelect],
    );

    const renderPrice = useCallback(
        (plan: FlatRatePlan) => {
            const pricing = values.plan.billingOwnership
                ? clientPricing.organizationOwnedNextSeatPricing
                : clientPricing.clientOwnedBillingPricing;

            const monetaryPrice = fromIntToMonetary(pricing[plan.plan]);

            const formatter = Number.isInteger(monetaryPrice)
                ? currencyFormatterNoFractions
                : currencyFormatter;

            const priceDetails =
                plan.plan === FLAT_RATE_PLANS.FREE ? "" : "Billed monthly";

            return (
                <>
                    <span className={priceSectionStyles.price}>
                        {formatter.format(monetaryPrice)}
                        <small>/m</small>
                    </span>

                    <span className={priceSectionStyles.priceDetails}>
                        {priceDetails}
                    </span>
                </>
            );
        },
        [clientPricing, values.plan.billingOwnership],
    );

    return (
        <>
            <Steps
                classNames={{
                    root: styles.steps,
                    panel: styles.panel,
                    list: styles.list,
                }}
                steps={steps}
                stepsRef={stepsRef}
                onStepChange={handleStepChange}
            />
            <AddClientModalFooter
                loadingAction={loadingAction}
                validators={stepValidators}
                currentStep={currentStep}
                onPrev={prev}
                onNext={next}
                onSkip={submitWithoutInvite}
                openPlansOverviewModal={() => setIsPlansOverviewModalOpen(true)}
                isPlanStepBlocked={isOrganizationOwnedPricingBlocked({
                    clientPricing,
                    isOrgOwned: values.plan.billingOwnership,
                })}
            />

            <StandardModal
                dialogClassName={styles.featuresModalDialog}
                show={isPlansOverviewModalOpen}
                onClose={() => setIsPlansOverviewModalOpen(false)}
            >
                <PlansOverviewModal
                    plans={plansForOverview}
                    renderAction={renderSelectPlanAction}
                    renderPrice={renderPrice}
                />
            </StandardModal>
        </>
    );
};
